/** @jsx jsx */

import React, {
  useState,
  useContext,
  useRef,
  useLayoutEffect,
  Fragment as _,
} from 'react';
import { css, jsx } from '@emotion/core';
import { colors, getButtonFocus } from 'styles/variables';
import { interactionContext as _interactionContext } from './interactionContext';
import { MultichoiceContext } from './Multichoice_v2';
import hexToRgb from 'services/hexToRGB';
import { Radio, Tick, NewTick } from './Radio';
import { getDefaultStyles } from './MultichoiceConfig';

const { lPink, lGrey0, lGrey4, lTeal0 } = colors;

const Answer = props => {
  const { id, children, style = {} } = props;

  // contexts
  const interactionContext = useContext<any>(_interactionContext);
  const multichoiceContext = useContext<any>(MultichoiceContext);

  //

  const multichoiceDefaultStyles = getDefaultStyles(
    interactionContext?.meta?.packageVersion
  );
  //

  // refs
  const answer = useRef<HTMLDivElement | null>(null);
  const label = useRef(null);
  const optionText = useRef<HTMLDivElement | null>(null);

  const {
    panelBackgroundOpacity: _panelBackgroundOpacity = 100,
    includeText = true,
    isMultichoiceMedia,
    mediaPanelWidth = multichoiceDefaultStyles?.mediaPanelWidth,
    mediaPanelHeight = multichoiceDefaultStyles?.mediaPanelHeight,
    fixedPanelHeight = multichoiceDefaultStyles?.fixedPanelHeight,
    selectedOutlineColor = '#000',
    includeRadioCheckbox: _includeRadioCheckbox_ = 1, // true
    focusColor = '#000',
  } = interactionContext.props;

  const includeRadioCheckbox = Boolean(_includeRadioCheckbox_);

  //
  let panelBackground = interactionContext.props?.panelBackground;
  //
  if (!panelBackground) {
    panelBackground = isMultichoiceMedia
      ? '#fff'
      : multichoiceDefaultStyles?.panelBackground;
  }
  //

  const { r, g, b } = hexToRgb(panelBackground);
  const panelBackgroundOpacity = _panelBackgroundOpacity * 0.01;
  panelBackground = `rgba(${r}, ${g}, ${b}, ${panelBackgroundOpacity})`;

  let { panelOutline } = interactionContext.props;

  if (isMultichoiceMedia && panelOutline === undefined) {
    panelOutline = true;
  }

  let { panelOutlineColor } = interactionContext.props;
  if (isMultichoiceMedia && !panelOutlineColor) {
    panelOutlineColor = lGrey4;
  }

  let panelBorderRadius = interactionContext.props.panelBorderRadius;
  if (panelBorderRadius === undefined) {
    panelBorderRadius = isMultichoiceMedia
      ? 20
      : multichoiceDefaultStyles?.panelBorderRadius;
  }

  //
  let dropShadow =
    interactionContext.props.dropShadow ?? multichoiceDefaultStyles?.dropShadow;
  //
  if (isMultichoiceMedia && interactionContext.props.dropShadow === undefined) {
    dropShadow = true;
  }
  //

  let optionTextColor = interactionContext.props?.optionTextColor;
  //
  if (!optionTextColor) {
    optionTextColor = isMultichoiceMedia
      ? '#000'
      : multichoiceDefaultStyles?.optionTextColor;
  }
  //

  let checkboxColor = optionTextColor;

  let {
    selected,
    correct,
    updateSelected,
    submit,
    isMultiSelect,
    answerCount,
  } = multichoiceContext;

  const isSelected = (selected as any).includes(id);

  correct = (correct as any).includes(id);
  const resolved = submit === 2;

  const [hasFocus, setHasFocus] = useState<boolean>(false);

  useLayoutEffect(() => {
    if (!optionText.current) {
      return;
    }
    //
    // remove the chameleon-text class
    // so only responds to local color
    const chameleonText = optionText.current.querySelector('.chameleon-text');
    if (chameleonText) {
      // can't use replace without polyfill for ie
      // this will do
      chameleonText.classList.remove('chameleon-text');
      chameleonText.classList.add('chameleon-text-multichoice-answer');
    }
  });

  let boxShadow: string | undefined = undefined;

  if (window.dev() && correct) {
    boxShadow = `0 0 0 1px #fff, 0 0 0 3px ${lPink}`;
  }

  if (dropShadow) {
    boxShadow = `${boxShadow ? `${boxShadow}, ` : ''}0px 0px ${
      isMultichoiceMedia ? 20 : 5
    }px rgba(0, 0, 0, 0.15)`;
  }

  let textToSpeechButton;

  if (answer.current) {
    textToSpeechButton = answer.current.querySelector(
      '[class*="TextToSpeech"]'
    );
  }

  const buttonFocus = getButtonFocus(
    isMultichoiceMedia ? focusColor : panelBackground
  )
    ?.split(':')[1]
    ?.trim()
    ?.replace(';', '');

  const optionStyles = {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 60,
    margin: '7.5px 0',
    outline: 'none',
    border: `1px solid
             ${
               panelOutline && panelOutlineColor
                 ? panelOutlineColor
                 : 'transparent'
             }`,
    borderRadius: panelBorderRadius,
    textAlign: 'left',
    background: panelBackground,

    color: optionTextColor,

    p: {
      color: optionTextColor,
    },

    li: {
      color: '#fff !important',
    },

    'li span': {
      color: optionTextColor,
    },

    boxShadow: hasFocus ? buttonFocus : boxShadow,
    transition: 'box-shadow 0.2s',
    ':hover': {
      '.checkbox-radio': {
        opacity: 0.8,
      },
    },

    ...(resolved && {
      pointerEvents: 'none',
    }),
  };

  if (isMultichoiceMedia) {
    //

    const [media, txt] = children[0].props.children;

    const mediaIsText = media?.props?.child === 0;
    const mediaIsImage = media?.props?.child === 1;

    let imgDescription;

    if (!includeText && mediaIsImage) {
      imgDescription = answer?.current?.querySelector('img')?.alt;
    }

    const eventHandlers = {
      onClick: window.dev()
        ? () => {}
        : (e: any) => {
            updateSelected(id);
          },
    };

    // //

    return (
      <div
        ref={answer}
        css={css({
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          width: mediaPanelWidth,
          height: fixedPanelHeight ? mediaPanelHeight : 'auto',
          border: `1px solid ${
            panelOutline && panelOutlineColor
              ? panelOutlineColor
              : 'transparent'
          }`,
          borderRadius: panelBorderRadius,
          background: panelBackground,
          overflow: 'hidden',
          boxShadow: hasFocus ? buttonFocus : boxShadow,

          ...(!includeRadioCheckbox &&
            selected.includes(id) && {
              boxShadow: `${
                hasFocus
                  ? `0 0 0 5px ${selectedOutlineColor}, 0 0 0 6px #fff, 0 0 0 8px ${focusColor}`
                  : `0 0 0 5px ${selectedOutlineColor}`
              }`,
            }),

          transition: 'box-shadow 0.2s',

          ...(!includeRadioCheckbox && {
            cursor: 'pointer',
          }),

          pointerEvents: submit === 0 ? 'auto' : 'none',
          ...(resolved && {
            pointerEvents: 'none',
          }),
        })}
        {...eventHandlers}
      >
        <div
          style={{
            ...(!mediaIsImage && {
              minHeight: 110,
            }),
            padding: mediaIsText ? '0 10px' : 0,
          }}
        >
          {media}
        </div>

        <div
          id={`i${id}`}
          css={css({
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            justifyContent: 'space-between',

            padding: `${window.dev() ? '0px' : '10px'} 10px 10px 10px`,
          })}
        >
          {!!includeText && (
            <div
              css={css({
                flex: 1,

                margin: 0,

                '.chameleon-text p': {
                  color: optionTextColor,
                },

                '&li': {
                  color: '#fff !important',
                },
                '&li span': {
                  color: optionTextColor,
                },
              })}
            >
              {window.dev() ? (
                txt
              ) : (
                <_>
                  <label
                    htmlFor={`input-${id}`}
                    css={css({
                      cursor: 'pointer',

                      '.chameleon-text': {
                        padding: '10px 15px',
                      },
                    } as any)}
                    onClick={e => {
                      e.stopPropagation();
                    }}
                  >
                    {txt}
                  </label>
                </_>
              )}
            </div>
          )}

          {imgDescription && (
            <label htmlFor={`input-${id}`} className="sr-only">
              {imgDescription}
            </label>
          )}

          {!resolved && (
            <input
              type={isMultiSelect ? 'checkbox' : 'radio'}
              id={`input-${id}`}
              className="visually-hidden"
              name={`input-${id}`}
              value={id}
              css={css({
                position: 'absolute',
                left: 0,
                top: 0,
                // transform: 'scale(0)',
              })}
              checked={selected.includes(id) ? true : false}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();

                  updateSelected(id);
                }
              }}
              onChange={(e: any) => {
                updateSelected(id);
              }}
            />
          )}

          {includeRadioCheckbox && (
            <div
              css={css({
                display: 'flex',
                justifyContent: 'center',
                width: '100%',
                margin: '10px 0 5px 0',

                input: {
                  pointerEvents: 'none',
                },

                cursor: 'pointer',
                pointerEvents: resolved ? 'none' : 'auto',
              })}
            >
              <Radio
                id={id}
                isMulti={isMultiSelect}
                isSquare={answerCount === 1}
                correct={correct}
                selected={(selected as any).includes(id)}
                updateSelected={updateSelected}
                resolved={resolved}
                checkboxColor={checkboxColor}
                style={style}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div id={`i${id}`}>
      {' '}
      {window.dev() ? (
        <div
          css={css({
            ...optionStyles,

            '.chameleon-text': {
              margin: '0 15px 0 5px',
            },

            overflow: 'hidden',
          } as any)}
        >
          {children}
          <Radio
            id={id}
            isMulti={isMultiSelect}
            isSquare={answerCount === 1}
            correct={correct}
            selected={(selected as any).includes(id)}
            updateSelected={updateSelected}
            resolved={resolved}
            checkboxColor={checkboxColor}
            style={{
              ...style,
            }}
          />
          <div style={{ width: 15 }} />
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <label
            htmlFor={`input-${id}`}
            css={css({
              ...optionStyles,

              cursor: 'pointer',

              '.chameleon-text > div:not([class*="TextToSpeech"])': {
                padding: '8px 50px 8px 15px',
              },

              '.chameleon-text [class*="TextToSpeech"]': {
                margin: '0px 0px 0px 15px',
              },

              overflow: 'hidden',
            } as any)}
            onClick={e => {
              e.stopPropagation();
            }}
          >
            {children}

            <div
              css={css({
                position: 'absolute',
                display: 'flex',
                alignItems: 'center',
                right: 15,
                top: 0,
                width: 20,
                height: '100%',

                pointerEvents: 'none',

                '*': {
                  pointerEvents: 'none',
                },
              })}
            >
              <Radio
                id={id}
                isMulti={isMultiSelect}
                isSquare={answerCount === 1}
                correct={correct}
                selected={(selected as any).includes(id)}
                updateSelected={updateSelected}
                resolved={resolved}
                checkboxColor={checkboxColor}
                style={style}
              />
            </div>
          </label>

          {!resolved && (
            <input
              type={isMultiSelect ? 'checkbox' : 'radio'}
              id={`input-${id}`}
              className="visually-hidden"
              name={`input-${id}`}
              value={id}
              css={css({
                position: 'absolute',
                left: 0,
                top: 0,
                // transform: 'scale(0)',
              })}
              checked={selected.includes(id) ? true : false}
              onFocus={() => {
                setHasFocus(true);
              }}
              onBlur={() => {
                setHasFocus(false);
              }}
              onKeyUp={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();

                  updateSelected(id);
                }
              }}
              onChange={(e: any) => {
                updateSelected(id);
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Answer;
